import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Waypoints, Presentation, HelpCircle } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { runTour, analysisTourSteps } from "@/lib/tours";
import { useAuth } from "@/context/AuthContext";
import OverviewTab from "@/components/hfacs/OverviewTab";
import ChronologyTab from "@/components/hfacs/ChronologyTab";
import FactorsTab from "@/components/hfacs/FactorsTab";
import CopilotTab from "@/components/hfacs/CopilotTab";
import CriticalPathsTab from "@/components/hfacs/CriticalPathsTab";
import HfixTab from "@/components/hfacs/HfixTab";
import StatusControl from "@/components/hfacs/StatusControl";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AnalysisDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canUseCopilot = user?.role === "Administrateur national";
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hfacs/${id}`);
      setAnalysis(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse introuvable.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!analysis) return;
    const t = setTimeout(() => runTour("analysis", analysisTourSteps(canUseCopilot)), 700);
    return () => clearTimeout(t);
  }, [analysis, canUseCopilot]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/dashboard" data-testid="analysis-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Toutes les analyses
        </Link>

        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : analysis ? (
          <>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <h1 data-testid="analysis-id-title" className="font-heading font-black uppercase tracking-tight text-3xl">
                {analysis.ID_HFACS}
                {analysis.ORIGINE === "Importée" && (
                  <span data-testid="analysis-imported-badge" className="ml-3 align-middle font-mono text-xs uppercase px-2.5 py-1 rounded-sm border border-accent/40 bg-accent/10 text-accent">
                    Importée
                  </span>
                )}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => runTour("analysis", analysisTourSteps(canUseCopilot), { force: true })}
                  data-testid="analysis-help-button"
                  title="Revoir la visite guidée"
                  className="p-2 rounded-sm border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
                <Link to={`/hfacs/${id}/diagramme`}>
                  <Button variant="outline" data-testid="analysis-open-diagram-button" className="gap-2 border-accent/40 text-accent hover:bg-accent/10">
                    <Waypoints className="h-4 w-4" />
                    Analyse causale
                  </Button>
                </Link>
                <Link to={`/hfacs/${id}/reunion`}>
                  <Button variant="outline" data-testid="analysis-open-meeting-button" className="gap-2 border-cyan-500/40 text-cyan-600 hover:bg-cyan-500/10">
                    <Presentation className="h-4 w-4" />
                    Mode Réunion
                  </Button>
                </Link>
              </div>
            </div>
            <div className="mb-6">
              <StatusControl analysis={analysis} onUpdated={load} />
            </div>
            <p className="text-sm text-muted-foreground mb-8">{analysis.TYPE_EVENEMENT}</p>

            <Tabs defaultValue="overview">
              <TabsList data-testid="analysis-tabs-list">
                <TabsTrigger value="overview" data-testid="analysis-tab-overview">Vue d'ensemble</TabsTrigger>
                <TabsTrigger value="chronology" data-testid="analysis-tab-chronology">Chronologie</TabsTrigger>
                <TabsTrigger value="factors" data-testid="analysis-tab-factors">Facteurs HFACS</TabsTrigger>
                {canUseCopilot && <TabsTrigger value="copilot" data-testid="analysis-tab-copilot">Copilote IA</TabsTrigger>}
                <TabsTrigger value="critical-paths" data-testid="analysis-tab-critical-paths">Chemins critiques</TabsTrigger>
                <TabsTrigger value="hfix" data-testid="analysis-tab-hfix">Plan HFIX</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-6">
                <OverviewTab analysis={analysis} onReportGenerated={load} />
              </TabsContent>
              <TabsContent value="chronology" className="mt-6">
                <ChronologyTab analysisId={id} />
              </TabsContent>
              <TabsContent value="factors" className="mt-6">
                <FactorsTab analysisId={id} />
              </TabsContent>
              {canUseCopilot && (
                <TabsContent value="copilot" className="mt-6">
                  <CopilotTab analysisId={id} isImported={analysis.ORIGINE === "Importée"} />
                </TabsContent>
              )}
              <TabsContent value="critical-paths" className="mt-6">
                <CriticalPathsTab analysisId={id} />
              </TabsContent>
              <TabsContent value="hfix" className="mt-6">
                <HfixTab analysisId={id} />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Analyse introuvable.</p>
        )}
      </main>
    </div>
  );
}
